/* ============================================================
   JUCE 3.0 · theme-service.js - tracking, returns, stockists.

   Three service pages own these controllers and no other page has their
   hooks. theme.js imports this file in the critical tier, and only when one
   of [data-track], [data-returns], [data-stockists] or [data-trade] is on the
   page; layout/theme.liquid modulepreloads it on exactly those pages (the
   same _svc test that decides service.css), so the controls bind as the page
   finishes parsing rather than a round trip later.

   HOW IT REACHES THEME.JS. The code below is theme.js's own, moved as it
   stood. What it still uses from theme.js arrives through mount(): the
   `let` list is filled once, from the kit theme.js hands over (chunkKit
   there), before any controller runs.
   Nothing here imports theme.js by URL: a URL without the ?v= digest
   would be a second copy of theme.js, booted twice.
   ============================================================ */
let closeChrome,
  cssAttr,
  dialogShow,
  dialogShut,
  reduceMotion,
  toast;

export function mount(k) {
  ({
    closeChrome,
    cssAttr,
    dialogShow,
    dialogShut,
    reduceMotion,
    toast
  } = k);
  return { initTrack, initReturns, initStockists, initTrade };
}

/* ============================================================
   Track parcel - the courier lookup.

   Replaces a 700-line merchant embed that pulled GSAP off a CDN to move one
   progress bar and inlined a second design system to draw the card. Nothing
   here is animated in JavaScript: the rail, the shimmer and the spinner are
   CSS, so the whole controller is state plus one fetch.

   IT NEVER BUILDS HTML FOR THE CARD. The shell is server-rendered (see
   snippets/track-parcel.liquid) and this fills the same nodes on every lookup,
   which is what makes a refresh update in place instead of flashing. Only the
   two genuinely repeating lists - the rail's steps and the day-grouped history
   - are constructed, and they go in through textContent, never innerHTML: the
   payload is a third party's and it is printed, not trusted.

   CRITICAL TIER, because the form owns a control. Without it a submit still
   navigates to ?ref=… and the page reads the same parameter on load, so the
   page works with the module missing entirely.
   ============================================================ */
function initTrack() {
  const root = document.querySelector('[data-track]');
  if (!root) return;

  /* A SETTING, not a constant - the reference is appended to it. Bob Go's
     current /v2/ API needs a Bearer key, and a theme cannot hold one (it is
     public), so the client calls the legacy unauthenticated route. Repointing
     this at our own proxy is a theme-editor change, not a code change. See the
     note at the top of snippets/track-parcel.liquid. */
  const API = root.dataset.trackApi || 'https://api.bobgo.co.za/tracking?tracking_reference=';
  const TTL = 60 * 1000;          /* a courier scan is minutes apart, not seconds */
  const TIMEOUT = 15000;
  const KEY = 'juce:track:v1';

  const el = {
    form:    root.querySelector('[data-track-form]'),
    input:   root.querySelector('.trk-find__input'),
    recent:  root.querySelector('[data-track-recent]'),
    chips:   root.querySelector('[data-track-recent-chips]'),
    result:  root.querySelector('[data-track-result]'),
    skel:    root.querySelector('[data-track-skel]'),
    fail:    root.querySelector('[data-track-fail]'),
    failNote: root.querySelector('[data-track-fail-note]'),
    failTitle: root.querySelector('[data-track-fail-title]'),
    card:    root.querySelector('[data-track-card]'),
    ref:     root.querySelector('[data-track-ref]'),
    order:   root.querySelector('[data-track-order]'),
    status:  root.querySelector('[data-track-status]'),
    eta:     root.querySelector('[data-track-eta]'),
    etaLabel: root.querySelector('[data-track-eta-label]'),
    courier: root.querySelector('[data-track-courier]'),
    logo:    root.querySelector('[data-track-courier-logo]'),
    updated: root.querySelector('[data-track-updated]'),
    rail:    root.querySelector('[data-track-rail]'),
    pickup:  root.querySelector('[data-track-pickup]'),
    log:     root.querySelector('[data-track-log]'),
    logCount: root.querySelector('[data-track-log-count]'),
    logBody: root.querySelector('[data-track-log-body]'),
    copy:    root.querySelector('[data-track-copy]'),
    refresh: root.querySelector('[data-track-refresh]'),
    call:    root.querySelector('[data-track-call]'),
    ask:     root.querySelector('[data-track-ask]')
  };
  if (!el.form || !el.card) return;

  let live = null;                /* the in-flight AbortController */
  let shown = null;               /* the normalised parcel on screen */

  /* ---- small local helpers ---- */
  const clean = (v) => String(v || '').trim().toUpperCase().replace(/[^A-Z0-9-]/g, '');
  const show = (node, on) => { if (node) node.hidden = !on; };
  const set = (node, text) => { if (node) node.textContent = text || ''; };

  const store = {
    read() { try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; } },
    write(v) { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* private mode */ } }
  };

  /* Dates arrive as "2026-08-19 07:59:13+00:00". Safari has historically
     refused that space, so it is normalised to a T before parsing - the old
     widget printed "Invalid Date" on iOS for exactly this reason. */
  const parse = (v) => {
    if (!v) return null;
    const d = new Date(String(v).replace(' ', 'T'));
    return isNaN(d) ? null : d;
  };
  const day = (v) => {
    const d = parse(v);
    return d ? d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  };
  const ago = (v) => {
    const d = parse(v);
    if (!d) return '';
    const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
    if (mins < 2) return 'just now';
    if (mins < 60) return 'updated ' + mins + ' min ago';
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return 'updated ' + hrs + (hrs === 1 ? ' hour ago' : ' hours ago');
    const days = Math.round(hrs / 24);
    return 'updated ' + days + (days === 1 ? ' day ago' : ' days ago');
  };

  /* ---- normalise ----
     Bob Go answers with a one-item list and puts the steps in an OBJECT keyed
     by slug, so the order is whatever the JSON engine felt like. step_number is
     the only thing that actually orders them. */
  function normalise(raw) {
    const d = (Array.isArray(raw) ? raw[0] : raw) || {};
    const steps = Object.keys(d.tracking_steps || {})
      .map((k) => Object.assign({ key: k }, d.tracking_steps[k]))
      .sort((a, b) => (+a.step_number || 99) - (+b.step_number || 99));

    /* "Where has it actually got to" is the LAST step marked done or current,
       not the count of done ones - a courier can mark a later step first. */
    let at = -1;
    steps.forEach((s, i) => { if (s.stage === 'done' || s.stage === 'current') at = i; });

    /* FINALISED IS DELIVERED (2026-10-21). SkyNet ends a parcel on
       "finalised", never "delivered" (UASLZXPNH: its last checkpoint reads
       "Delivered POD"), and Bob Go leaves delivered_time blank, so the card
       sat on "Finalised / Estimated arrival: Not available yet". Either word
       is a parcel in the shopper's hands, landed at that checkpoint's time. */
    const arrived = /^(delivered|finali[sz]ed)$/i.test(String(d.status || '').trim());
    const landedAt = (function () {
      const hit = (Array.isArray(d.checkpoints) ? d.checkpoints : [])
        .filter((c) => /^(delivered|finali[sz]ed)/i.test(String(c.status || '')))
        .pop();
      return hit ? hit.time : '';
    }());

    const loc = d.delivery_location || {};
    const isPickup = d.has_ready_for_pickup_status ||
      d.service_level_delivery_type === 'locker' || d.service_level_delivery_type === 'counter';

    return {
      ref: d.shipment_tracking_reference || d.id || '',
      order: d.custom_order_name || d.channel_order_number || d.order_number || '',
      status: arrived ? 'Delivered' : (d.status_friendly || 'In progress'),
      state: arrived ? 'delivered' : (d.status || ''),
      courier: {
        name: d.courier_name || '',
        logo: d.courier_logo || '',
        phone: d.courier_phone || ''
      },
      /* A DELIVERED PARCEL HAS NO ESTIMATE, and Bob Go blanks the two window
         fields the moment it lands - so a card that keeps printing "Estimated
         arrival" reads as "we still do not know" about something that is
         already in the shopper's hands. Once it is delivered the line becomes
         the fact instead of the forecast, off shipment_movement_events (the
         checkpoint list is the fallback for older shipments). */
      eta: (function () {
        const moved = d.shipment_movement_events || {};
        if (arrived) {
          return day(moved.delivered_time || landedAt || d.last_checkpoint_time) || 'Delivered';
        }
        if (moved.ready_for_pickup_time) {
          return day(moved.ready_for_pickup_time) || 'Waiting for you';
        }
        const from = day(d.shipment_estimated_delivery_date_from);
        const to = day(d.shipment_estimated_delivery_date_to);
        if (from && to && from !== to) return from + ' to ' + to;
        return from || to || 'Not available yet';
      }()),
      etaLabel: arrived
        ? 'Delivered'
        : ((d.shipment_movement_events || {}).ready_for_pickup_time ? 'Ready for collection' : 'Estimated arrival'),
      updated: ago(d.last_checkpoint_time || d.shipment_time_created),
      steps: steps,
      at: at,
      pickup: isPickup ? {
        provider: loc.pickup_point_provider_name || d.pickup_point_provider_name || '',
        logo: loc.pickup_point_provider_logo_url || d.pickup_point_provider_logo || '',
        name: loc.name || d.pickup_point_header || 'Collection point',
        type: loc.type || d.pickup_point_location_type || d.service_level_delivery_type || '',
        address: loc.address || '',
        hours: loc.trading_hours || '',
        steps: Array.isArray(d.pickup_point_instructions) ? d.pickup_point_instructions : [],
        map: (loc.lat && loc.lng)
          ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(loc.lat + ',' + loc.lng)
          : ''
      } : null,
      days: (Array.isArray(d.grouped_checkpoints) ? d.grouped_checkpoints : [])
        .map((g) => ({
          date: g.date || '',
          hits: (Array.isArray(g.checkpoints) ? g.checkpoints : [])
            .filter((c) => (c.message || '').trim() || (c.location || '').trim())
        }))
        .filter((g) => g.hits.length)
    };
  }

  /* ---- paint ---- */
  const TICK = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.6 10.4l3.6 3.6 7.6-7.6"/></svg>';

  function paintRail(p) {
    el.rail.textContent = '';
    el.rail.style.setProperty('--trk-steps', String(p.steps.length || 1));
    p.steps.forEach((step, i) => {
      const li = document.createElement('li');
      li.className = 'trk-step' + (i < p.at ? ' is-done' : '') +
        (i === p.at ? (p.state === 'delivered' ? ' is-done' : ' is-now') : '');

      const dot = document.createElement('span');
      dot.className = 'trk-step__dot';
      dot.setAttribute('aria-hidden', 'true');
      dot.innerHTML = TICK;                       /* our own markup, not the payload */

      const text = document.createElement('span');
      text.className = 'trk-step__text';
      const label = document.createElement('span');
      label.className = 'trk-step__label';
      label.textContent = step.label || step.key || '';
      text.append(label);
      if (step.time) {
        const when = document.createElement('span');
        when.className = 'trk-step__time';
        when.textContent = day(step.time);
        text.append(when);
      }
      li.append(dot, text);
      el.rail.append(li);
    });
  }

  function row(iconPath, text) {
    const wrap = document.createElement('p');
    wrap.className = 'trk-pickup__row';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 20 20'); svg.setAttribute('width', '18'); svg.setAttribute('height', '18');
    svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.5'); svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round'); svg.setAttribute('aria-hidden', 'true');
    iconPath.forEach((d) => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', d); svg.append(path);
    });
    const span = document.createElement('span');
    span.textContent = text;
    wrap.append(svg, span);
    return wrap;
  }
  const PIN = ['M10 17.3c3.2-3.6 4.8-6.3 4.8-8.3a4.8 4.8 0 1 0-9.6 0c0 2 1.6 4.7 4.8 8.3Z', 'M11.9 8.8a1.9 1.9 0 1 1-3.8 0 1.9 1.9 0 0 1 3.8 0Z'];
  const CLOCK = ['M17.2 10a7.2 7.2 0 1 1-14.4 0 7.2 7.2 0 0 1 14.4 0Z', 'M10 5.9V10l2.9 1.8'];

  function paintPickup(p) {
    if (!p.pickup) { show(el.pickup, false); return; }
    const k = p.pickup;
    el.pickup.textContent = '';

    const head = document.createElement('div');
    head.className = 'trk-pickup__head';
    if (k.logo) {
      const img = document.createElement('img');
      img.className = 'trk-pickup__logo'; img.src = k.logo; img.alt = '';
      img.loading = 'lazy'; img.decoding = 'async';
      head.append(img);
    }
    const names = document.createElement('div');
    const name = document.createElement('div');
    name.className = 'trk-pickup__name';
    name.textContent = k.name;
    names.append(name);
    if (k.type) {
      const type = document.createElement('div');
      type.className = 'trk-pickup__type';
      type.textContent = k.provider ? k.provider + ' · ' + k.type : k.type;
      names.append(type);
    }
    head.append(names);
    if (k.map) {
      const a = document.createElement('a');
      a.className = 'btn btn--secondary btn--sm trk-pickup__map';
      a.href = k.map; a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.textContent = 'Directions';
      head.append(a);
    }
    el.pickup.append(head);
    if (k.address) el.pickup.append(row(PIN, k.address));
    if (k.hours) el.pickup.append(row(CLOCK, k.hours));
    if (k.steps.length) {
      const ol = document.createElement('ol');
      ol.className = 'trk-pickup__steps';
      k.steps.forEach((s) => {
        const li = document.createElement('li');
        li.textContent = s;
        ol.append(li);
      });
      el.pickup.append(ol);
    }
    show(el.pickup, true);
  }

  function paintLog(p) {
    el.logBody.textContent = '';
    let n = 0;
    p.days.forEach((g) => {
      const box = document.createElement('div');
      box.className = 'trk-day';
      const date = document.createElement('p');
      date.className = 'trk-day__date';
      date.textContent = g.date;
      box.append(date);
      g.hits.forEach((c) => {
        n += 1;
        const hit = document.createElement('div');
        hit.className = 'trk-hit';
        const time = document.createElement('span');
        time.className = 'trk-hit__time';
        time.textContent = c.time_string || '';
        const dot = document.createElement('span');
        dot.className = 'trk-hit__dot';
        dot.setAttribute('aria-hidden', 'true');
        const body = document.createElement('span');
        const msg = document.createElement('span');
        msg.className = 'trk-hit__msg';
        msg.textContent = c.message || c.status_friendly || '';
        body.append(msg);
        if (c.location) {
          const loc = document.createElement('span');
          loc.className = 'trk-hit__loc';
          loc.textContent = c.location;
          body.append(document.createElement('br'), loc);
        }
        hit.append(time, dot, body);
        box.append(hit);
      });
      el.logBody.append(box);
    });
    set(el.logCount, n ? String(n) : '');
    show(el.log, n > 0);
  }

  function paint(p) {
    shown = p;
    set(el.ref, p.ref);
    set(el.order, p.order);
    show(el.order, !!p.order);
    set(el.status, p.status);
    set(el.etaLabel, p.etaLabel);
    set(el.eta, p.eta);
    set(el.courier, p.courier.name);
    set(el.updated, p.updated);
    if (p.courier.logo) { el.logo.src = p.courier.logo; el.logo.alt = p.courier.name; show(el.logo, true); }
    else show(el.logo, false);
    if (p.courier.phone) { el.call.href = 'tel:' + p.courier.phone.replace(/\s+/g, ''); show(el.call, true); }
    else show(el.call, false);

    paintRail(p);
    paintPickup(p);
    paintLog(p);

    /* The chat seam has to carry the parcel, or Jennie opens on a blank
       question the shopper has to type out again. Written here rather than at
       bind time for the same reason the finder rewrites its own prompt. */
    if (el.ask) {
      el.ask.setAttribute('data-chat-prompt',
        'I am tracking parcel ' + p.ref + (p.order ? ' for order ' + p.order : '') +
        '. It says "' + p.status + '". Can you tell me what is happening with it?');
    }

    show(el.skel, false); show(el.fail, false); show(el.card, true); show(el.result, true);
  }

  function fail(title, note) {
    set(el.failTitle, title);
    set(el.failNote, note);
    show(el.skel, false); show(el.card, false); show(el.fail, true); show(el.result, true);
  }

  /* ---- recent references ---- */
  function paintRecent() {
    const list = (store.read().recent || []).filter(Boolean).slice(0, 4);
    el.chips.textContent = '';
    list.forEach((ref) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'trk-chip'; b.textContent = ref;
      b.addEventListener('click', () => { el.input.value = ref; look(ref, false); });
      el.chips.append(b);
    });
    show(el.recent, list.length > 0);
  }
  function remember(ref) {
    const s = store.read();
    const list = [ref].concat((s.recent || []).filter((r) => r !== ref)).slice(0, 4);
    store.write(Object.assign(s, { recent: list }));
    paintRecent();
  }

  /* ---- the lookup ---- */
  function cached(ref) {
    const c = (store.read().cache || {})[ref];
    return c && (Date.now() - c.t) < TTL ? c.d : null;
  }
  function cache(ref, data) {
    const s = store.read();
    /* One entry per reference, four references deep - a tracking cache that
       grows without a bound is how localStorage quotas get hit on shared
       machines. */
    const box = s.cache || {};
    box[ref] = { t: Date.now(), d: data };
    const keys = Object.keys(box).sort((a, b) => box[b].t - box[a].t).slice(0, 4);
    s.cache = keys.reduce((o, k) => { o[k] = box[k]; return o; }, {});
    store.write(s);
  }

  function look(raw, fresh) {
    const ref = clean(raw);
    if (!ref || ref.length < 4) {
      fail('That does not look like a reference', 'A Bob Go reference is at least four characters, like UAE99PBLB. It is in the shipping email we sent when the parcel left us.');
      return;
    }
    el.input.value = ref;
    url(ref);

    if (!fresh) {
      const hit = cached(ref);
      if (hit) { paint(normalise(hit)); return; }
    }

    show(el.card, false); show(el.fail, false); show(el.skel, true); show(el.result, true);

    if (live) live.abort();
    live = new AbortController();
    const guard = setTimeout(() => live.abort(), TIMEOUT);

    fetch(API + encodeURIComponent(ref), { signal: live.signal, headers: { Accept: 'application/json' } })
      .then((r) => r.json().then((body) => ({ ok: r.ok, body: body })).catch(() => ({ ok: false, body: null })))
      .then((res) => {
        /* Bob Go answers a missing reference with a 500 whose body is a JSON
           string containing another JSON object. Both the status and the shape
           mean "not found", so neither is treated as an outage. */
        const list = Array.isArray(res.body) ? res.body : null;
        if (!res.ok || !list || !list.length) {
          fail('No parcel on that reference',
            'Nothing is moving under ' + ref + ' yet. A brand new order can take a few hours to appear, and the reference is the one in the shipping email rather than the order number.');
          return;
        }
        cache(ref, list);
        remember(ref);
        paint(normalise(list));
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') {
          fail('That took too long', 'The courier did not answer in time. Your connection may be slow, or their system may be busy - try again in a moment.');
        } else {
          fail('We could not reach the courier', 'Something went wrong on the way to the tracking service. Try again, or ask Jennie and she will look it up from our side.');
        }
      })
      .finally(() => { clearTimeout(guard); live = null; });
  }

  /* Keeps the address shareable and the back button honest. replaceState on a
     repeat so a refresh does not stack ten identical entries. */
  function url(ref) {
    const next = new URL(location.href);
    const same = next.searchParams.get('ref') === ref;
    if (ref) next.searchParams.set('ref', ref); else next.searchParams.delete('ref');
    next.searchParams.delete('tracking_reference');
    history[same ? 'replaceState' : 'pushState']({ ref: ref }, '', next);
  }

  /* ---- wiring ---- */
  el.form.addEventListener('submit', (e) => { e.preventDefault(); look(el.input.value, true); });

  root.addEventListener('click', (e) => {
    const pick = e.target.closest('[data-track-pick]');
    if (pick) { e.preventDefault(); look(pick.dataset.trackPick, false); root.querySelector('.trk-result').scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' }); return; }
    if (e.target.closest('[data-track-retry]')) { show(el.result, false); el.input.focus(); return; }
    if (e.target.closest('[data-track-refresh]') && shown) {
      const btn = e.target.closest('[data-track-refresh]');
      btn.classList.add('is-spinning');
      setTimeout(() => btn.classList.remove('is-spinning'), 900);
      look(shown.ref, true);
      return;
    }
    const copy = e.target.closest('[data-track-copy]');
    if (copy && shown) {
      const done = () => { copy.classList.add('is-done'); toast('Reference copied'); setTimeout(() => copy.classList.remove('is-done'), 1600); };
      if (navigator.clipboard) navigator.clipboard.writeText(shown.ref).then(done, () => toast('Could not copy'));
      else done();
    }
  });

  window.addEventListener('popstate', () => {
    const ref = new URL(location.href).searchParams.get('ref');
    if (ref) look(ref, false); else show(el.result, false);
  });

  /* ---- YOUR PARCELS, when Liquid could not answer here ----

     The list is server work and stays server work: snippets/trk-parcels.liquid
     renders it into [data-track-mine] on first paint and this never runs. But
     `customer.orders` is only DOCUMENTED as populated in the customer account
     templates, and this is a page template, so the array can come back empty
     on a shopper who plainly has orders. That failure is silent - an empty
     list looks exactly like "you have no parcels" - which is the same shape
     of bug as the `order.fulfillments` property that never existed.

     So: if the slot came back empty and window.theme says this browser is
     signed in WITH orders, ask Shopify to render the same section against
     /account, where the array is not in question. Sections do not have to
     belong to the template they are requested against.

     The shopper's own cookie is the authorisation - signed out, /account
     redirects and `redirect: 'manual'` turns that into an opaque response we
     abandon without downloading the login page. Nothing here can ask about
     anybody else's orders, which is why it is Shopify's session and not the
     unverified identity claim in window.theme. */
  const mine = root.querySelector('[data-track-mine]');
  const who = (window.theme && window.theme.customer) || {};
  if (mine && !mine.children.length && who.signed_in && (who.orders || 0) > 0) {
    const home = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(home + 'account?section_id=trk-parcels', {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
      redirect: 'manual'
    })
      .then((res) => (res.ok ? res.text() : ''))
      .then((html) => {
        if (!html || html.indexOf('trk-mine__list') === -1) return;
        mine.innerHTML = html;
        /* born after initReveal ran - never leave them gated (same as the
           carousel clones; without this the rows sit at opacity 0 forever) */
        mine.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('reveal'));
      })
      .catch(() => { /* the reference field above still works */ });
  }

  /* ---- first paint ----
     `tracking_reference` is the parameter the OLD page used and it is still in
     shipping emails and WhatsApp messages sent months ago, so it keeps
     working; `ref` is what everything writes now. */
  paintRecent();
  const q = new URL(location.href).searchParams;
  const opening = q.get('ref') || q.get('tracking_reference');
  if (opening) look(opening, false);
}

/* ============================================================
   Returns.

   WHEN: on press. The old embed pulled a 65KB stylesheet, Inter off Google
   Fonts and eleven ES modules on every visit to the returns page, including
   the visits that only wanted to know how long they had. Nothing is requested
   until somebody actually starts a return.

   WHAT: assets/rma.js, in this page, talking to JENNIE - the same server the
   chat widget already uses. There is no frame, no third-party stylesheet and
   no asset proxy any more, so there is also no CSS collision to defend
   against and no other document to reach into.

   The widget is built once and kept. Closing the sheet mid-application and
   reopening it must not throw away three steps of typing.
   ============================================================ */
function initReturns() {
  const root = document.querySelector('[data-returns]');
  if (!root) return;
  const sheet = root.querySelector('[data-returns-sheet]');
  const host = root.querySelector('[data-returns-frame]');
  const load = root.querySelector('[data-returns-load]');
  if (!sheet || !host) return;

  /* The contract around this is UNCHANGED - [data-returns], the sheet, the
     open and close controls, the ?start deep link, the Ask Jennie foot - so
     the two Liquid snippets that mount it did not have to move when the
     third-party portal came out. */

  /* The root's own attribute first: returns must keep working on a shop
     that has the chat widget turned off, and window.theme.jennie is absent
     entirely in that case. */
  const endpoint = root.dataset.returnsApi
    || (window.theme && window.theme.jennie && window.theme.jennie.endpoint) || '';
  let mounted = false;
  let loading = false;

  /* ONE RETURNS FORM, AND IT IS OURS.

     This used to ask JENNIE whether returns were open and fall back to a
     third party's ten-step application at fohmu.com/RMA when the answer was
     no - loaded into a srcdoc iframe through a PHP asset proxy, with its
     header hidden by !important because its stylesheet collided with ours.
     The fallback existed so nobody met a broken page while the two halves of
     the cutover shipped on different schedules.

     On Juce-3.0 that gap is closed: this theme IS the new returns system, so
     there is nothing to fall back to and nothing to ask. The dial, the
     ?rma=new escape hatch and the whole iframe are gone with it.

     WHAT THIS THEME NOW ASSUMES: that rma.enabled is on by the time it is
     published. With the switch off the server answers 503 and the widget says
     returns are closed and offers Jennie - honest, and a dead end for a
     shopper who wanted to send a bottle back. It is the right assumption for
     a theme that is the new system, and it is the one thing to check before
     publishing this theme rather than after. */
  async function build() {
    if (mounted || loading) return;
    loading = true;
    try {
      const mod = await import(root.dataset.returnsModule || '/assets/rma.js');
      mod.mount(host, { endpoint, previewKey: root.dataset.returnsPreview || '' });
      mounted = true;
      if (load) load.remove();
    } catch (err) {
      /* A failed import is the only thing left that can go wrong here, and
         the honest answer is the one thing that still works. */
      if (load) {
        load.innerHTML = '';
        const p = document.createElement('p');
        p.textContent = 'The returns page will not load. Jennie can log the return for you instead.';
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'btn btn--sm';
        b.setAttribute('data-chat-open', '');
        b.setAttribute('data-chat-prompt', 'The returns page will not load. Can you log a return for me?');
        b.textContent = 'Ask Jennie';
        load.append(p, b);
      }
    } finally {
      loading = false;
    }
  }

  function open() {
    build();
    dialogShow(sheet);
  }

  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-returns-open]')) { e.preventDefault(); open(); return; }
    if (e.target.closest('[data-returns-close]')) { e.preventDefault(); dialogShut(sheet); }
  });
  /* Deep link, so a "start your return" in an email or a Jennie message can
     land straight on the open portal.

     ?rma=debug OPENS IT TOO, and that is the whole entry point for the
     fixture mode. A returns form has fourteen screens and most of them are
     unreachable without a real order, a real photograph and a real courier
     quote - so in practice nobody looked at them, and the screens nobody
     could reach are the ones that were wrong. The widget reads the same
     parameter and mounts on fixtures; this only has to open the sheet.

     ?rma=debug&live=1 is the same panel against the real API, which creates
     real drafts - which is why it is a second opt-in rather than the
     default. */
  const qs = new URL(location.href).searchParams;
  if (qs.has('start') || String(qs.get('rma') || '') === 'debug') open();
}

/* ============================================================
   Stockists - the store locator.

   37 stockists in one JSON file. Everything - search, province, scent, the
   two shelf flags, distance - is a filter and a sort over that array, so
   there is no server round trip after the first fetch and no debounce worth
   the name; the whole pass is sub-millisecond at this size.

   THE MAP IS LOADED ON INTENT, never on load. Leaflet plus its stylesheet
   plus a tile session is the heaviest thing this page can do and it is worth
   nothing to somebody reading a list on a phone. Desktop asks for it when the
   map column scrolls into view; mobile asks when the Map tab is pressed.

   GEOLOCATION IS A BUTTON. The page it replaces called navigator.geolocation
   during boot, which is how a site trains people to deny it permanently.
   ============================================================ */
function initStockists() {
  const root = document.querySelector('[data-stockists]');
  if (!root) return;

  const el = {
    seek: root.querySelector('[data-stk-seek]'),
    clear: root.querySelector('[data-stk-clear]'),
    near: root.querySelector('[data-stk-near]'),
    nearLabel: root.querySelector('[data-stk-near-label]'),
    province: root.querySelector('[data-stk-province]'),
    scent: root.querySelector('[data-stk-scent]'),
    count: root.querySelector('[data-stk-count]'),
    reset: root.querySelector('[data-stk-reset]'),
    list: root.querySelector('[data-stk-list]'),
    empty: root.querySelector('[data-stk-empty]'),
    skel: root.querySelector('[data-stk-skel]'),
    online: root.querySelector('[data-stk-online]'),
    mapPane: root.querySelector('[data-stk-map-pane]'),
    map: root.querySelector('[data-stk-map]'),
    mapHint: root.querySelector('[data-stk-map-hint]'),
    mapLoad: root.querySelector('[data-stk-map-load]'),
    sheet: root.querySelector('[data-stk-sheet]'),
    sheetTitle: root.querySelector('[data-stk-sheet-title]'),
    sheetSub: root.querySelector('[data-stk-sheet-sub]'),
    sheetBody: root.querySelector('[data-stk-sheet-body]')
  };
  if (!el.list) return;

  const FEED = root.dataset.stkFeed;
  const PIN = root.dataset.stkPin;
  /* THE TWO SETTINGS THE FEED CANNOT CARRY - see the long note in
     snippets/stockists.liquid. Nothing in store_locations.json expresses a
     bottle SIZE, so which shelves carry the 10ml and which scent is missing
     from that 10ml range are both typed in the theme editor and read here.
     Compared case-insensitively and trimmed, because these are names a human
     types into a text field. */
  const list2set = (v) => new Set(String(v || '').split(',')
    .map((x) => x.trim().toLowerCase()).filter(Boolean));
  const MINI_GROUPS = list2set(root.dataset.stkMiniGroups);
  const MINI_NOT = list2set(root.dataset.stkMiniNot);
  /* HOW MANY COUNTERS A "NEAREST" FRAME HOLDS. Three is the smallest number
     that still shows a choice - one pin is a dot on a grey field with no sense
     of how far anything is - and it keeps the frame tight enough that the
     nearest counter is legible at street level rather than being one of
     thirty-one specks over the whole country. */
  const MAP_NEAR = 3;

  let all = [];        /* physical stockists */
  let web = [];        /* online retailers */
  let view = [];       /* what is on screen */
  let here = null;     /* {lat, lng} once the shopper asks */
  let picked = null;
  const flags = { testers: false, full: false, mini: false };

  let map = null, layer = null, marks = new Map(), mapReady = false;

  /* The map is sticky BELOW the pinned control bar, and the bar's height is
     content (it wraps differently at every width), so it is measured rather
     than guessed - the same move --head-h makes for the header. The CSS
     fallback (10rem) only matters for the frame before this runs. */
  const bar = root.querySelector('.stk-bar');
  if (bar && 'ResizeObserver' in window) {
    const setBarH = () => root.style.setProperty('--stk-bar-h', bar.offsetHeight + 'px');
    new ResizeObserver(setBarH).observe(bar);
    setBarH();
  }

  /* ---- distance ----
     Haversine on a sphere. South Africa is 1600km across, so the ellipsoid
     correction is under 0.3% here and nobody drives to a shop by great
     circle anyway - the number is a SORT KEY that happens to be printable. */
  function far(a, b) {
    const R = 6371;
    const p = Math.PI / 180;
    const dLat = (b.lat - a.lat) * p, dLng = (b.lng - a.lng) * p;
    const s = Math.sin(dLat / 2) ** 2 +
      Math.cos(a.lat * p) * Math.cos(b.lat * p) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  }

  /* ---- load ----
     The feed is Jennie's /api/stockists when she is configured (see the
     snippet); the Shopify Files copy is the safety net, so an API outage
     costs one failed request and nothing visible. */
  const FALLBACK_FEED = 'https://cdn.shopify.com/s/files/1/0621/7403/6164/files/store_locations.json';
  const pull = (url) => fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('http ' + r.status))))
    .then((rows) => (Array.isArray(rows) && rows.length ? rows : Promise.reject(new Error('empty feed'))));
  pull(FEED)
    .catch((err) => (FEED !== FALLBACK_FEED ? pull(FALLBACK_FEED) : Promise.reject(err)))
    .then((rows) => {
      const norm = (Array.isArray(rows) ? rows : []).map((s, i) => ({
        id: 'stk-' + i,
        name: s.Store_name || s.Franschise || 'Stockist',
        group: s.Franschise || '',
        address: s.Address_string || '',
        province: s.Province || '',
        lat: Number(s.Latitude), lng: Number(s.Longitude),
        range: s.Range || '',
        full: /full/i.test(s.Range || ''),
        testers: /tester/i.test(s.Testers || ''),
        samples: /free sample/i.test(s.Testers || ''),
        testerLabel: s.Testers || '',
        logo: s.Logo || '',
        products: Array.isArray(s.Products) ? s.Products : [],
        online: String(s.Type || '').trim().toLowerCase() === 'online'
      }));
      /* The 10ml range, per store, derived once. `mini` is whether this
         group's shelves carry minis at all; `minis` is the scent list that
         range actually holds, which is the store's own Products minus the
         names the owner has said have no 10ml. Both are computed here rather
         than inside the filter so the count, the chip, the card tag and the
         sheet are all reading one answer. */
      norm.forEach((s) => {
        s.mini = MINI_GROUPS.has(String(s.group || '').trim().toLowerCase());
        s.minis = s.mini
          ? s.products.filter((n) => !MINI_NOT.has(String(n).trim().toLowerCase()))
          : [];
      });
      web = norm.filter((s) => s.online);
      all = norm.filter((s) => !s.online && s.lat && s.lng);
      if (el.skel) el.skel.hidden = true;
      /* The chip only exists if somebody in this feed carries a mini. A filter
         that empties the list on every press is a broken control, not a
         narrow one. */
      const miniChip = root.querySelector('[data-stk-flag="mini"]');
      if (miniChip) miniChip.hidden = !norm.some((s) => s.mini);
      fillPicks();
      apply();                 /* paints the list, the map and the online band */
      watchMap();
      /* Sort by distance WITHOUT being asked (owner, 2026-08-19: "find
         stockist must automatically try and get location") - the browser
         still shows its own permission ask, and a refusal is silent: the
         alphabetical list was already correct. The button stays as the
         manual retry and the off switch. */
      askWhere(true);
    })
    .catch(() => {
      if (el.skel) el.skel.hidden = true;
      if (el.count) el.count.textContent = 'The stockist list is not loading right now.';
      if (el.empty) {
        el.empty.hidden = false;
        el.empty.textContent = 'We could not load the stockist list. Refresh in a moment, or browse the full range online.';
      }
    });

  /* ---- the two selects, built from the data ----
     Province options are the ones that actually occur, so a province with no
     counter is never offered and then apologised for. The scent list is the
     union of every shelf - it can only ever offer something somebody has. */
  function fillPicks() {
    const provinces = Array.from(new Set(all.map((s) => s.province).filter(Boolean))).sort();
    provinces.forEach((p) => {
      const o = document.createElement('option');
      o.value = p; o.textContent = p;
      el.province.append(o);
    });
    const scents = Array.from(new Set(all.flatMap((s) => s.products))).sort();
    scents.forEach((n) => {
      const o = document.createElement('option');
      o.value = n; o.textContent = n;
      el.scent.append(o);
    });
  }

  /* ---- filter and sort ---- */
  function apply() {
    const q = (el.seek.value || '').trim().toLowerCase();
    const prov = el.province.value;
    const want = el.scent.value;

    view = all.filter((s) => {
      if (prov && s.province !== prov) return false;
      if (flags.testers && !(s.testers || s.samples)) return false;
      if (flags.full && !s.full) return false;
      if (flags.mini && !s.mini) return false;
      /* THE SCENT TEST READS THE 10ml RANGE WHEN THE 10ml CHIP IS ON, which is
         the only way "Mystic Haze, in a 10ml" can honestly come back empty: it
         is on every one of those shelves in 50ml and on none of them in 10ml,
         so testing s.products would send a shopper to a counter that cannot
         sell them what they asked for. */
      const shelf = flags.mini ? s.minis : s.products;
      if (want && shelf.indexOf(want) === -1) return false;
      if (!q) return true;
      return (s.name + ' ' + s.group + ' ' + s.address + ' ' + s.province).toLowerCase().indexOf(q) > -1 ||
        shelf.some((p) => p.toLowerCase().indexOf(q) > -1);
    });

    if (here) {
      view.forEach((s) => { s.km = far(here, s); });
      view.sort((a, b) => a.km - b.km);
    } else {
      view.sort((a, b) => (a.province || '').localeCompare(b.province || '') || a.name.localeCompare(b.name));
    }

    paintList();
    paintMap();
    paintOnline();

    const on = !!(q || prov || want || flags.testers || flags.full || flags.mini);
    if (el.reset) el.reset.hidden = !on;
    if (el.clear) el.clear.hidden = !q;
    if (el.count) {
      el.count.textContent = view.length === 0
        ? 'No counters match'
        : view.length + (view.length === 1 ? ' counter' : ' counters') +
          (on ? ' of ' + all.length : '') +
          (want ? ' with ' + want : '') +
          (flags.mini ? ' in 10ml' : '') +
          (here ? ', nearest first' : '');
    }
  }

  /* EQUALISE THE MARKS BY INK AREA, NOT BY ONE NUMBER (owner, 2026-08-24:
     "medirite and beutiy needs to be abit smaller and WE ARE EGG abit
     bigger"). The feed ships two families that no single size can settle:
     wordmarks at 6.8:1 (Medirite) and 7.7:1 (Beauty on TApp), and a 1:1
     roundel (We Are Egg). A shared height cap makes the wordmarks tiny slivers
     and a shared width rail makes them tower over the roundel, which is the
     state this replaces.

     So each mark is measured and sized on its own: h = k / ratio^0.4. The
     square-root form is true area-equalisation; the gentler exponent pulls the
     widest marks back from collapsing to a hairline, and in practice lands
     both wordmarks about a fifth smaller and the roundel about a fifth bigger,
     all three within a hair of the same ink area. The clamp is the guard
     against a freak file the merchant uploads later.

     naturalWidth is only there once the bytes are, and these are lazy images
     far down a list, so it runs on load as well as immediately - and it writes
     a custom property rather than a height so the CSS keeps the fallback,
     the rem shelf and the rail cap. */
  function fitMark(img, k) {
    const put = () => {
      const r = img.naturalWidth / img.naturalHeight;
      if (!isFinite(r) || r <= 0) return;
      const h = Math.min(2.6, Math.max(.85, (k || 2.25) / Math.pow(r, .45)));
      img.style.setProperty('--stk-mark-h', h.toFixed(3) + 'rem');
    };
    if (img.complete && img.naturalWidth) put();
    else img.addEventListener('load', put, { once: true });
  }

  function tag(text, kind) {
    const s = document.createElement('span');
    s.className = 'stk-tag' + (kind ? ' stk-tag--' + kind : '');
    s.textContent = text;
    return s;
  }

  const EMPTY_SAY = el.empty ? el.empty.textContent : '';
  function paintList() {
    el.list.textContent = '';
    el.empty.hidden = view.length > 0;
    /* NAME THE FILTER THAT EMPTIED IT. "Try a wider province, or drop the
       scent filter" is the right advice for most empty results and the wrong
       advice for this one: a scent with no 10ml is not going to appear in a
       wider province, and a shopper who reads that will keep trying. */
    if (!view.length && el.empty) {
      el.empty.textContent = (flags.mini && el.scent.value)
        ? el.scent.value + ' is not made in a 10ml, so no shelf carries it in that size. It is on all of these in 50ml - drop the 10ml filter to see them.'
        : EMPTY_SAY;
    }
    const frag = document.createDocumentFragment();
    view.forEach((s) => {
      const li = document.createElement('li');
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'stk-card' + (picked === s.id ? ' is-on' : '');
      card.dataset.stkCard = s.id;

      /* THE LOGO IS THE BRAND LINE (owner, 2026-08-19). It used to be a 36px
         grayscale chip on the left with the group's name printed again on the
         right - and at 36px, dimmed, half the marks were unreadable, so the
         row leaned on the text and the picture was decoration. Now it is one
         thing instead of two: the mark rides the right edge at its real size
         and in its real colour, and the printed group is gone. Wordmarks are
         wide and roundels are square, so the box is a MAX and contain does the
         rest - no crop, no stretch, whichever shape the brand sent.

         The mark came DOWN a step on 2026-08-21 (owner: "reduce the logos
         abit"), and on 2026-08-24 it stopped being one size for every file:
         fitMark below reads each logo's own aspect and gives it a height that
         holds the same INK AREA as its neighbours, because a rail and a cap
         between them were printing the wordmarks at twice the roundel. The
         width and height attributes are gone with it - the CSS now sets height
         from the measured aspect and leaves width to contain, so any fixed
         pair here would just be a wrong shape for the frame before the bytes
         land. Nothing shifts either way: the rail is a grid column, so the
         mark can only ever resize inside its own cell. */
      if (s.logo) {
        const img = document.createElement('img');
        img.className = 'stk-card__logo'; img.src = s.logo;
        img.alt = s.group || '';
        img.loading = 'lazy'; img.decoding = 'async';
        fitMark(img);
        card.append(img);
      } else {
        /* No mark: the group still has to be readable, so it falls back to
           text in the same slot rather than leaving the row anonymous. */
        const box = document.createElement('span');
        box.className = 'stk-card__logo stk-card__logo--text';
        box.textContent = s.group || '';
        card.append(box);
      }

      const name = document.createElement('span');
      name.className = 'stk-card__name'; name.textContent = s.name;
      card.append(name);

      /* Province joins the distance line. It is the one fact the removed
         group text carried that the mark cannot: a logo says We Are Egg, it
         does not say Western Cape. */
      const far_ = document.createElement('span');
      far_.className = 'stk-card__far';
      far_.textContent = [
        s.km != null ? (s.km < 10 ? s.km.toFixed(1) : Math.round(s.km)) + ' km' : '',
        s.province || ''
      ].filter(Boolean).join(' · ');
      card.append(far_);

      const tags = document.createElement('span');
      tags.className = 'stk-tags';
      if (s.testers) tags.append(tag('Testers', 'has'));
      else if (s.samples) tags.append(tag('Free samples', 'has'));
      if (s.full) tags.append(tag('Full range', 'full'));
      else if (s.range) tags.append(tag(s.range));
      /* Only while the shopper is asking about minis. Marking every one of
         these rows all the time would put a fourth constant on a row that is
         already carrying three, and the answer is only interesting to somebody
         who asked the question. */
      if (flags.mini && s.mini) tags.append(tag('10ml', 'has'));
      card.append(tags);

      li.append(card);
      frag.append(li);
    });
    el.list.append(frag);
  }

  /* ---- THE ONLINE STRIP ANSWERS THE 10ml QUESTION TOO ----
     Four of the five shelves the owner named as carrying minis are in this
     band rather than in the list above it - the counters are one group, We Are
     Egg - so a 10ml filter that only narrowed the list would leave the shopper
     reading six online logos with no way to tell which two of them cannot help.
     It is the one filter this band takes: province and distance mean nothing to
     a website, and the scent picker is answered by the list. */
  function paintOnline() {
    if (!el.online) return;
    el.online.textContent = '';
    const show = flags.mini ? web.filter((s) => s.mini) : web;
    show.forEach((s) => {
      const a = document.createElement('a');
      a.className = 'stk-web';
      a.href = /^https?:/i.test(s.address) ? s.address : '#';
      a.target = '_blank'; a.rel = 'noopener noreferrer';
      if (s.logo) {
        const img = document.createElement('img');
        img.className = 'stk-web__logo'; img.src = s.logo; img.alt = '';
        img.loading = 'lazy'; img.decoding = 'async';
        a.append(img);
      }
      const n = document.createElement('span');
      n.className = 'stk-web__name'; n.textContent = s.group || s.name;
      a.append(n);
      /* Full range is the norm across the online six, so only the exception
         gets a caption - six identical FULL RANGE lines were noise. While the
         10ml chip is on, every mark in this band carries minis by definition,
         so the caption names the SIZE of the range instead: it is the one fact
         the shopper asked for and it is not the same for every logo, because
         the ones it is not true of are no longer here. */
      if (flags.mini) {
        const note = document.createElement('span');
        note.className = 'stk-web__note';
        note.textContent = s.minis.length + ' in 10ml';
        a.append(note);
      } else if (!s.full) {
        const note = document.createElement('span');
        note.className = 'stk-web__note';
        note.textContent = s.range || 'Selected scents';
        a.append(note);
      }
      el.online.append(a);
    });
  }

  /* ---- the sheet ---- */
  function line(head, build) {
    const sec = document.createElement('div');
    sec.className = 'stk-sec';
    const h = document.createElement('span');
    h.className = 'stk-sec__head'; h.textContent = head;
    sec.append(h);
    build(sec);
    return sec;
  }

  function openSheet(s) {
    picked = s.id;
    el.sheetTitle.textContent = s.name;
    el.sheetSub.textContent = s.group + (s.province ? ' · ' + s.province : '') +
      (s.km != null ? ' · ' + (s.km < 10 ? s.km.toFixed(1) : Math.round(s.km)) + ' km away' : '');
    el.sheetBody.textContent = '';

    el.sheetBody.append(line('Address', (sec) => {
      const p = document.createElement('p');
      p.textContent = s.address || 'Address not listed';
      const a = document.createElement('a');
      a.className = 'btn btn--secondary btn--sm';
      a.href = 'https://www.google.com/maps/dir/?api=1&destination=' +
        encodeURIComponent(s.address || (s.lat + ',' + s.lng));
      a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.textContent = 'Directions';
      /* ---- IT HAS TO LEAVE THE DIALOG BEFORE IT CAN LEAVE THE PAGE ----
         (owner, 2026-09-01: "Stockist, on iPhone when click on directions it
         doesn't open in new tab, the modal just goes down slightly everytime".)

         The anchor was already a real link with target=_blank and a rel, and
         on a desktop it has always worked. This sheet is a <dialog> opened
         with showModal(), which puts it in the TOP LAYER, and Safari on iOS
         will not open a target=_blank navigation out of one: the tap is
         treated as not-user-initiated, the popup is dropped silently, and the
         only thing that visibly happens is the button taking focus - which
         scrolls the dialog by a few pixels. That scroll is the "goes down
         slightly", and it is why it looked like a gesture problem rather than
         a blocked navigation.

         So the link leaves the top layer first. close() is synchronous and
         still inside the same user gesture, so window.open is a legitimate
         popup by the time it runs, and location.href catches the browser that
         blocks it anyway. The href stays on the element either way, which is
         what keeps long-press, Copy link and open-in-new-tab working, and what
         makes this a no-op if the script never runs. */
      a.addEventListener('click', (ev) => {
        if (ev.defaultPrevented || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button > 0) return;
        ev.preventDefault();
        const href = a.href;
        try { if (el.sheet.open) el.sheet.close(); } catch (_) { /* not a dialog */ }
        const win = window.open(href, '_blank', 'noopener');
        if (!win) window.location.href = href;
      });
      sec.append(p, a);
    }));

    el.sheetBody.append(line('On the shelf', (sec) => {
      const p = document.createElement('p');
      p.textContent = [s.range, s.testerLabel].filter(Boolean).join(' · ');
      sec.append(p);
    }));

    const shelf = flags.mini && s.mini ? s.minis : s.products;
    if (shelf.length) {
      const want = el.scent.value;
      const head = flags.mini && s.mini
        ? shelf.length + ' of our scents in 10ml'
        : shelf.length + ' of our scents';
      el.sheetBody.append(line(head, (sec) => {
        const wrap = document.createElement('div');
        wrap.className = 'stk-scents';
        /* The scent being filtered for goes first and wears the fill: it is
           the reason this shopper opened this store. */
        const order = want ? [want].concat(shelf.filter((n) => n !== want)) : shelf;
        order.forEach((n) => {
          const c = document.createElement('span');
          c.className = 'stk-scent' + (n === want ? ' is-want' : '');
          c.textContent = n;
          wrap.append(c);
        });
        sec.append(wrap);
      }));
    }

    dialogShow(el.sheet);
    paintList();
  }

  /* ---- the map ---- */
  function loadLeaflet() {
    if (window.L) return Promise.resolve(window.L);
    if (loadLeaflet.p) return loadLeaflet.p;
    loadLeaflet.p = new Promise((done, die) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      css.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      css.crossOrigin = '';
      document.head.append(css);

      const js = document.createElement('script');
      js.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      js.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      js.crossOrigin = '';
      js.onload = () => done(window.L);
      js.onerror = () => die(new Error('leaflet'));
      document.head.append(js);
    });
    return loadLeaflet.p;
  }

  function bootMap() {
    if (mapReady) return Promise.resolve();
    mapReady = true;
    if (el.mapHint) el.mapHint.textContent = 'Loading the map';
    if (el.mapLoad) el.mapLoad.disabled = true;
    return loadLeaflet().then((L) => {
      /* Wheel zoom ON (owner, 2026-08-19: "I can't zoom in and out using my
         mouse?"). The trade-off is that a wheel over the map zooms instead
         of scrolling the page - on this page the map is beside the list,
         not across the scroll path, so the page still scrolls fine. */
      map = L.map(el.map, { preferCanvas: true, scrollWheelZoom: true })
        .setView([-29, 24.5], 5);
      /* "Leaflet |" ahead of the OSM credit is the library advertising itself.
         The data credit stays - that one is owed. */
      map.attributionControl.setPrefix(false);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap', maxZoom: 18
      }).addTo(map);
      layer = L.layerGroup().addTo(map);
      if (el.mapLoad) el.mapLoad.remove();
      paintMap();
    }).catch(() => {
      mapReady = false;
      if (el.mapLoad) el.mapLoad.disabled = false;
      if (el.mapHint) el.mapHint.textContent = 'The map is not answering - the list has every address';
    });
  }

  function paintMap() {
    if (!map || !window.L) return;
    const L = window.L;
    layer.clearLayers();
    marks.clear();
    const icon = PIN
      ? L.icon({ iconUrl: PIN, iconSize: [38, 38], iconAnchor: [19, 38], popupAnchor: [0, -34] })
      : null;
    const pts = [];
    view.forEach((s) => {
      const m = L.marker([s.lat, s.lng], icon ? { icon: icon } : undefined);
      const html = document.createElement('div');
      const n = document.createElement('span');
      n.className = 'stk-pop__name'; n.textContent = s.name;
      const sub = document.createElement('span');
      sub.className = 'stk-pop__sub'; sub.textContent = s.group;
      const go = document.createElement('a');
      go.className = 'stk-pop__go'; go.href = '#'; go.textContent = 'Details';
      go.addEventListener('click', (e) => { e.preventDefault(); openSheet(s); });
      html.append(n, sub, go);
      m.bindPopup(html);
      m.addTo(layer);
      marks.set(s.id, m);
      pts.push([s.lat, s.lng]);
    });
    /* YOU, AS A DOT AND NOT AS A PIN. The pins are shops; a sixth-of-a-pin
       lookalike where the shopper is standing is the one marker on this map
       that must not be mistaken for somewhere to drive to. It goes in the same
       layer as the rest so the next repaint clears it with them. */
    if (here) {
      L.circleMarker([here.lat, here.lng], {
        radius: 7, weight: 3, color: '#111', opacity: 1,
        fillColor: '#fff', fillOpacity: 1, interactive: false
      }).addTo(layer);
    }
    frameMap();
  }

  /* ---- WHERE THE MAP OPENS ----
     (owner, 2026-08-31: "when switch map it just zoom out and doesn't show
     nearest".)

     It did, and the reason is worth stating because the old line looks correct:
     fitBounds over every pin PLUS the shopper, with maxZoom 11. maxZoom is a
     ceiling, not a floor - fitBounds picks whatever zoom makes the bounds fit,
     and the bounds were thirty-one counters from Cape Town to Polokwane, so it
     picked 5. Measured on the phone harness at 375 with the shopper 2.4km from
     the nearest counter: tile z=5, the whole country, all thirty-one pins on
     screen and no way to tell which one was 2.4km away.

     So when we know where the shopper is, the frame is the shopper plus the
     nearest MAP_NEAR counters and nothing else. `view` is already sorted by
     distance when `here` is set, so "nearest" is a slice rather than a second
     sort. The far pins stay on the map - panning out reaches them, which is
     what panning is for - they just stop deciding the opening frame.

     With no location it is the old behaviour, and that is right: the answer to
     "where do you have counters" when we do not know where you are IS the
     country. */
  function frameMap() {
    if (!map || !window.L) return;
    const on = view.filter((s) => s.lat && s.lng);
    if (!on.length) return;
    if (here) {
      const box = on.slice(0, MAP_NEAR).map((s) => [s.lat, s.lng]);
      box.push([here.lat, here.lng]);
      map.fitBounds(box, { padding: [40, 40], maxZoom: 13 });
      return;
    }
    map.fitBounds(on.map((s) => [s.lat, s.lng]), { padding: [36, 36], maxZoom: 9 });
  }

  /* THREE WAYS IN, because deferring something has to be different from
     losing it. Desktop pulls the map when its column is genuinely on screen,
     or the moment a pointer crosses the pane; mobile waits for the tab; and
     the button inside the empty pane is the one that never depends on an
     observer firing. All of them are intent - none of them is boot. */
  function watchMap() {
    if (!el.mapPane) return;
    el.mapPane.addEventListener('pointerenter', () => bootMap(), { once: true });
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && matchMedia('(min-width: 1024px)').matches) {
        io.disconnect();
        bootMap();
      }
    }, { rootMargin: '200px' });
    io.observe(el.mapPane);
  }

  /* ---- wiring ---- */
  let typing = null;
  el.seek.addEventListener('input', () => {
    clearTimeout(typing);
    typing = setTimeout(apply, 120);       /* one frame's grace, not a network debounce */
  });
  el.clear.addEventListener('click', () => { el.seek.value = ''; el.seek.focus(); apply(); });
  el.province.addEventListener('change', apply);
  el.scent.addEventListener('change', apply);

  root.addEventListener('click', (e) => {
    const flag = e.target.closest('[data-stk-flag]');
    if (flag) {
      const k = flag.dataset.stkFlag;
      flags[k] = !flags[k];
      flag.setAttribute('aria-pressed', flags[k] ? 'true' : 'false');
      apply();
      return;
    }
    const card = e.target.closest('[data-stk-card]');
    if (card) {
      const s = view.find((x) => x.id === card.dataset.stkCard);
      if (s) {
        openSheet(s);
        if (map && marks.has(s.id)) { map.setView([s.lat, s.lng], 14); marks.get(s.id).openPopup(); }
      }
      return;
    }
    if (e.target.closest('[data-stk-sheet-close]')) { dialogShut(el.sheet); return; }
    if (e.target.closest('[data-stk-reset]')) {
      el.seek.value = ''; el.province.value = ''; el.scent.value = '';
      flags.testers = false; flags.full = false; flags.mini = false;
      root.querySelectorAll('[data-stk-flag]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      apply();
      return;
    }
    /* SCOPED TO THE SWITCH, and the root's own state lives on a DIFFERENT
       attribute (data-stk-mode). Both matter: a bare
       closest('[data-stk-view]') walks all the way up to the section, and the
       section is where the chosen mode is written - so every click anywhere on
       the page matched, was treated as a tab press, and swallowed the click
       before it could reach the map button or Near me. */
    const tab = e.target.closest('.stk-switch [data-stk-view]');
    if (tab) {
      const which = tab.dataset.stkView;
      root.dataset.stkMode = which;
      root.querySelectorAll('.stk-switch [data-stk-view]').forEach((b) => {
        const on = b === tab;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      /* invalidateSize FIRST, THEN THE FRAME. The pane was display:none until
         this click, so Leaflet's cached size is 0x0 and any fitBounds computed
         against it lands on the wrong zoom. Re-framing after the measurement
         is what makes the Map tab open where the shopper is standing rather
         than where the map happened to be. */
      if (which === 'map') bootMap().then(() => { if (map) { map.invalidateSize(); frameMap(); } });
      return;
    }
    if (e.target.closest('[data-stk-map-load]')) { bootMap(); return; }
    if (e.target.closest('[data-stk-near]')) askWhere();
  });

  el.sheet.addEventListener('close', () => { picked = null; paintList(); });

  /* ---- TAKE ME TO THE NEAREST ONE ----
     (owner, 2026-08-31: "also nearest cta doesn't work".)

     IT USED TO BE AN OFF SWITCH, and that is the whole defect. The page asks
     for a location on load (askWhere(true)), so by the time a shopper reaches
     this button it is almost always already lit and reading "Nearest first" -
     which meant the ONLY thing pressing it could do was throw the distance
     sort away and go back to alphabetical. Nobody presses a button labelled
     "Nearest first" in order to stop seeing the nearest one first. Pressed in
     map view, where the question is loudest, it did the one thing the shopper
     was not asking for.

     So the second press now means what the label says: frame the map on you
     and the counters beside you, open the nearest one's pin, and put its row
     in the middle of the list. The off switch is gone deliberately rather than
     moved somewhere else - the alphabetical order it returned to is not
     something anybody asks for by name, and the province filter and Clear
     filters are still the ways to change what the list holds.

     auto=true is the on-load ask: it never jumps the page anywhere and it
     fails SILENTLY - a toast about a permission the shopper just declined is a
     scolding. The pressed button keeps its voice. */
  function goNearest() {
    const first = view.filter((s) => s.lat && s.lng)[0];
    if (!first) { toast('Nothing matches those filters yet'); return; }
    bootMap().then(() => {
      if (!map) return;
      map.invalidateSize();
      frameMap();
      if (marks.has(first.id)) marks.get(first.id).openPopup();
    });
    /* The list is display:none in map view on a phone, where this is a no-op
       and the map above is the whole answer. In list view it is the answer. */
    const card = el.list.querySelector('[data-stk-card="' + cssAttr(first.id) + '"]');
    if (card) card.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  function askWhere(auto) {
    if (here) {
      if (auto) return;
      goNearest();
      return;
    }
    if (!navigator.geolocation) { if (!auto) toast('This browser will not share a location'); return; }
    el.near.classList.add('is-busy');
    if (!auto) el.nearLabel.textContent = 'Finding you';
    navigator.geolocation.getCurrentPosition((pos) => {
      here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      el.near.classList.remove('is-busy');
      el.near.classList.add('is-on');
      el.nearLabel.textContent = 'Nearest first';
      apply();
      /* apply() has already re-framed a live map through paintMap. A pressed
         ask also takes the shopper there; the on-load one does not, because
         nobody asked it to move anything. */
      if (!auto) goNearest();
    }, () => {
      el.near.classList.remove('is-busy');
      el.nearLabel.textContent = 'Near me';
      if (!auto) toast('We could not get your location - the province filter works just as well');
    }, { enableHighAccuracy: false, timeout: 9000, maximumAge: 5 * 60 * 1000 });
  }

  root.dataset.stkMode = 'list';
}

/* ============================================================
   Stock JUCE - the trade application.

   Three steps and a verdict. The gate is the whole point (owner, 2026-08-19:
   "we only focusing on established chains and companies, inviddual selling
   not"), so the rules are stated on step one and checked in one place -
   judge() - rather than smeared across the fields.

   AND THE GATE IS ONE LINE WIDE, not four (owner, same day, on seeing it:
   "we are quite flexible as long as it is not just ONE person trading
   situation, but a established brand"). The first cut refused a single shop,
   a business under a year old, and anybody on a Gmail address - rules that
   would turn away a registered boutique with one door and a year on the
   clock, while keeping out nobody willing to pick a different answer from a
   dropdown. So
   the footprint questions stay, because a person reading the brief wants
   them, and they stopped being gates. What is refused is what was actually
   meant: one person reselling, which is not a business a wholesale account
   can be opened with.

   PASSING DOES NOT FILE A FORM. It opens Jennie with the entire application
   already written out, because the answer to a trade enquiry is a
   conversation with somebody, not a receipt. The brief is shown on screen
   BEFORE it is sent: handing a person's own details to an agent without
   letting them read the message first is not something to do quietly.

   Nothing is stored and nothing is posted anywhere from here. The only
   destination is the chat panel the shopper opens themselves.
   ============================================================ */
function initTrade() {
  const dialog = document.querySelector('[data-trade]');
  if (!dialog) return;
  const form = dialog.querySelector('[data-trade-form]');
  const meter = dialog.querySelector('[data-trade-meter]');
  const count = dialog.querySelector('[data-trade-count]');
  const next = dialog.querySelector('[data-trade-next]');
  const back = dialog.querySelector('[data-trade-back]');
  const foot = dialog.querySelector('[data-trade-foot]');
  const body = dialog.querySelector('[data-trade-body]');
  const brief = dialog.querySelector('[data-trade-brief]');
  const pass = dialog.querySelector('[data-trade-pass]');
  const fail = dialog.querySelector('[data-trade-fail]');
  const why = dialog.querySelector('[data-trade-fail-why]');
  const LAST = 3;
  let step = 1;

  const steps = () => Array.from(dialog.querySelectorAll('[data-trade-step]'));
  const paneOf = (n) => dialog.querySelector('[data-trade-step="' + n + '"]');

  const LABELS = {
    kind: 'Business', doors: 'Doors', years: 'Years trading',
    company: 'Registered name', reg: 'Registration no', vat: 'VAT no',
    site: 'Website', trading: 'Trading as', where: 'Trades in',
    brands: 'Already carries', volume: 'First order',
    name: 'Contact', role: 'Role', email: 'Email', phone: 'Phone'
  };
  const KINDS = {
    chain: 'Retail chain or group', independent: 'Independent retail store',
    online: 'Online retailer or marketplace', distributor: 'Distributor or wholesaler',
    services: 'Salon, spa or pharmacy group', individual: 'Individual reseller'
  };
  /* The <select> values are terse on purpose (they are what judge() and any
     later reader compare against). These are the same answers in words, and
     they are what the shopper reads back in the brief and what the buyer
     reads in the chat - "1 doors" and "0 years trading" were neither. */
  const DOORS = {
    '1': 'one store', '2-4': 'two to four stores', '5-9': 'five to nine stores',
    '10-24': 'ten to twenty four stores', '25+': 'twenty five or more stores',
    '0': 'online only'
  };
  const YEARS = {
    '0': 'under a year', '1-2': 'one to two years',
    '3-5': 'three to five years', '6+': 'six or more years'
  };
  const words = (k, raw) => (
    k === 'kind' ? (KINDS[raw] || raw) :
    k === 'doors' ? (DOORS[raw] || raw) :
    k === 'years' ? (YEARS[raw] || raw) : raw
  );

  function values() {
    const out = {};
    new FormData(form).forEach((v, k) => { out[k] = typeof v === 'string' ? v.trim() : v; });
    return out;
  }

  /* ---- per-field validation ----
     Native constraint validation does the ordinary work; these are the three
     shapes the browser has no opinion about. */
  /* The hint under a field is its resting text and an error only BORROWS the
     slot, so both paths have to remember what was there. Seeding it in
     complain() alone was not enough: calm() runs over every field that
     validates, including ones that never complained, and it was writing an
     empty string over "South African CIPC number." the first time anybody
     pressed Continue. */
  function baseMsg(msg) {
    if (msg.dataset.base === undefined) msg.dataset.base = msg.textContent;
    return msg.dataset.base;
  }
  function complain(field, text) {
    const wrap = field.closest('.field, .tra-check');
    const msg = wrap && wrap.querySelector('[data-msg]');
    field.setAttribute('aria-invalid', 'true');
    if (msg) {
      baseMsg(msg);
      msg.textContent = text;
      msg.classList.add('is-error');
    }
  }
  function calm(field) {
    const wrap = field.closest('.field, .tra-check');
    const msg = wrap && wrap.querySelector('[data-msg]');
    field.removeAttribute('aria-invalid');
    if (msg) { msg.textContent = baseMsg(msg); msg.classList.remove('is-error'); }
  }

  function checkField(f) {
    const v = (f.value || '').trim();
    if (f.type === 'checkbox') {
      if (!f.checked) { complain(f, 'We need this to take the enquiry further.'); return false; }
      calm(f); return true;
    }
    if (!v) { complain(f, 'This one is needed.'); return false; }
    if (f.name === 'reg' && !/^\d{4}\s*\/\s*\d{4,7}\s*\/\s*\d{2}$/.test(v) && !/^[A-Z]{1,3}\d{6,}$/i.test(v)) {
      complain(f, 'That is not a registration number. South African ones look like 2019/123456/07.');
      return false;
    }
    if (f.type === 'url' && !/^https?:\/\/[^\s.]+\.[^\s]{2,}$/i.test(v)) {
      complain(f, 'A full address please, starting https://');
      return false;
    }
    if (f.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) {
      complain(f, 'That email does not look right.');
      return false;
    }
    if (f.type === 'tel' && v.replace(/\D/g, '').length < 9) {
      complain(f, 'A number we can actually reach you on.');
      return false;
    }
    calm(f);
    return true;
  }

  function checkStep(n) {
    const pane = paneOf(n);
    if (!pane) return true;
    let ok = true, first = null;
    pane.querySelectorAll('[data-trade-field]').forEach((f) => {
      if (!checkField(f)) { ok = false; if (!first) first = f; }
    });
    if (first) first.focus();
    return ok;
  }

  /* ---- THE GATE ----
     ONE line, and it is about who is trading rather than how big they are.
     Scale is not the test: a single shop with its own door, a young company,
     an online retailer are all businesses, and all of them get through to a
     conversation where a person can look at the registration number, the
     website and the shelf they already carry. What does not get through is a
     person reselling under their own name - there is no company to open an
     account with, no shopfront to stock, and the wholesale terms are built
     around a business.

     The registered name, the registration number and the website are still
     REQUIRED on step one, so "established" is something the form asks for
     rather than something judge() has to infer from a Gmail address. */
  function judge(v) {
    if (v.kind === 'individual') {
      return 'We supply registered businesses that trade under their own name rather than individuals reselling. That is the one line we do not move on.';
    }
    return null;
  }

  function paintBrief(v) {
    brief.textContent = '';
    const order = ['company', 'trading', 'kind', 'reg', 'vat', 'doors', 'years', 'where', 'site', 'brands', 'volume', 'name', 'role', 'email', 'phone'];
    order.forEach((k) => {
      if (!v[k]) return;
      const row = document.createElement('div');
      row.className = 'tra-brief__line';
      const key = document.createElement('span');
      key.className = 'tra-brief__k'; key.textContent = LABELS[k] || k;
      const val = document.createElement('span');
      val.className = 'tra-brief__v';
      val.textContent = words(k, v[k]);
      row.append(key, val);
      brief.append(row);
    });
  }

  function message(v) {
    return [
      'Trade enquiry - I would like to stock JUCE.',
      '',
      'Business: ' + (v.company || '') + (v.trading && v.trading !== v.company ? ' (trading as ' + v.trading + ')' : ''),
      'Type: ' + (KINDS[v.kind] || v.kind),
      'Registration: ' + (v.reg || '') + (v.vat ? ', VAT ' + v.vat : ''),
      'Footprint: ' + words('doors', v.doors) + ', trading ' + words('years', v.years) + ', ' + (v.where || ''),
      'Website: ' + (v.site || ''),
      'Already carries: ' + (v.brands || ''),
      v.volume ? 'First order in mind: ' + v.volume : '',
      '',
      'Contact: ' + (v.name || '') + ', ' + (v.role || ''),
      (v.email || '') + ' · ' + (v.phone || ''),
      '',
      'Could you tell me about wholesale terms, minimums and lead times?'
    ].filter((l) => l !== null).join('\n');
  }

  function show(n) {
    step = n;
    steps().forEach((p) => { p.hidden = p.dataset.tradeStep !== String(n); });
    const done = n === 'verdict';
    if (back) back.hidden = done || n === 1;
    if (foot) foot.hidden = done;
    /* The verdict is not a step, so it does not get a step number - the foot
       is hidden there anyway, but a screen reader still reaches the text. */
    if (count) count.textContent = done ? '' : 'Step ' + n + ' of ' + LAST;
    if (next) next.textContent = n === LAST ? 'Check my details' : 'Continue';
    if (meter) meter.style.transform = 'scaleX(' + (done ? 1 : n / LAST) + ')';
    body.scrollTop = 0;
    const pane = paneOf(n);
    const first = pane && pane.querySelector('input, select, textarea, button');
    if (first && matchMedia('(pointer: fine)').matches) first.focus();
  }

  function decide() {
    const v = values();
    const no = judge(v);
    pass.hidden = !!no;
    fail.hidden = !no;
    if (no) { why.textContent = no; }
    else { paintBrief(v); }
    show('verdict');
  }

  /* ---- wiring ---- */
  next.addEventListener('click', () => {
    if (!checkStep(step)) return;
    if (step === LAST) decide();
    else show(step + 1);
  });
  back.addEventListener('click', () => { if (step !== 'verdict' && step > 1) show(step - 1); });
  dialog.addEventListener('click', (e) => {
    if (e.target.closest('[data-trade-close]')) { e.preventDefault(); dialogShut(dialog); return; }
    if (e.target.closest('[data-trade-edit]')) { show(1); return; }
    if (e.target.closest('[data-trade-send]')) {
      const text = message(values());
      /* WITH THE CHAT OFF, THE BRIEF STILL HAS TO GO SOMEWHERE. Jennie is a
         setting, and a trade enquiry that has been typed out in full and then
         swallowed because a flag was off is the worst outcome this dialog can
         produce. Same words, addressed to the trade inbox instead. */
      const chat = window.theme && window.theme.jennie && window.theme.jennie.module;
      if (!chat) {
        location.href = 'mailto:hello@juce.co.za?subject=' +
          encodeURIComponent('Trade enquiry - ' + (values().company || 'Stock JUCE')) +
          '&body=' + encodeURIComponent(text);
        return;
      }
      dialogShut(dialog);
      /* The seam contract, so this goes through exactly the same door as every
         other "chat with us" in the theme - see initJennie. `ask` sends it
         outright rather than prefilling: the shopper has just read the brief
         and pressed a button labelled with what it does. */
      document.dispatchEvent(new CustomEvent('juce:chat', {
        detail: { prompt: text, ask: true, source: 'trade' }
      }));
    }
  });
  /* ENTER MEANS CONTINUE, and it must never mean submit. The shell is a
     <form method="dialog">, which exists so the browser hands back native
     field semantics and autofill - but a dialog form that submits CLOSES the
     dialog, and three steps of typing would go with it. Today there is more
     than one text field in the form at all times, so the implicit-submission
     rule does not fire; that is an accident of the field list rather than a
     guarantee, and one step losing an input would quietly turn Enter into a
     data-shredder. Caught explicitly, and pointed at the button the shopper
     meant. */
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (step !== 'verdict') next.click();
  });
  form.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const t = e.target;
    if (t.tagName === 'TEXTAREA' || t.tagName === 'BUTTON') return;   /* newline, and its own press */
    e.preventDefault();
    if (step !== 'verdict') next.click();
  });

  /* Typing is the apology. Clearing the complaint on input rather than on the
     next press means the field stops shouting the moment it is being fixed. */
  form.addEventListener('input', (e) => {
    const f = e.target.closest('[data-trade-field]');
    if (f && f.getAttribute('aria-invalid') === 'true') checkField(f);
  });

  /* Every trigger in the theme: header, footer, menu sheet, stockists page.
     They all carry a real href to the wholesale page, which is what runs when
     this module is not there. */
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-trade-open]');
    if (!open) return;
    e.preventDefault();
    closeChrome();
    if (!dialog.open) { show(1); dialogShow(dialog); }
  });

  show(1);
}
